import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const privacyContent = `Política de Privacidad

Última actualización: febrero 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la información
Tu información se utiliza exclusivamente para:
• Procesar y entregar tu compra (el PDF del kit).
• Enviarte actualizaciones relacionadas con tu compra.
• Responder a solicitudes de soporte.

3. Compartir información
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu pago o cuando la ley lo requiera.

4. Cookies
Nuestro sitio puede utilizar cookies esenciales para el funcionamiento básico. No utilizamos cookies de seguimiento publicitario.

5. Seguridad
Implementamos medidas de seguridad estándar de la industria para proteger tu información personal.

6. Tus derechos
Puedes solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento contactándonos directamente.

7. Cambios a esta política
Nos reservamos el derecho de actualizar esta política. Cualquier cambio será publicado en esta página con la fecha de actualización correspondiente.`;
const termsContent = `Términos y Condiciones

Última actualización: febrero 2026

1. Producto
El "Shadowban & Jail Survival Kit" es un producto digital (PDF) que contiene información educativa y orientativa para creadores de contenido. No garantiza resultados específicos en ninguna plataforma.

2. Entrega
El producto se entrega en formato PDF descargable inmediatamente después de completar la compra.

3. Uso permitido
El kit es para uso personal y no puede ser redistribuido, revendido, compartido públicamente ni reproducido sin autorización expresa.

4. Reembolsos
Si después de leer y ejecutar el contenido del kit sientes que no te proporcionó claridad operativa, puedes solicitar un reembolso según las políticas de la plataforma de pago utilizada.

5. Limitación de responsabilidad
Este kit ofrece orientación educativa basada en experiencia y buenas prácticas. No somos responsables por decisiones tomadas en base a este contenido ni por cambios en las políticas de plataformas de terceros (TikTok, Meta, etc.).

6. Propiedad intelectual
Todo el contenido del kit está protegido por derechos de autor. Queda prohibida su reproducción total o parcial sin consentimiento.

7. Aceptación
Al realizar la compra, aceptas estos términos y condiciones en su totalidad.`;
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) {
  return <AnimatePresence data-id="element-26">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-27">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-28" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="relative max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-[#141414] p-8 shadow-2xl" data-id="element-29">
            <div className="mb-6 flex items-center justify-between" data-id="element-30">
              <h3 className="text-xl font-bold text-white" data-id="element-31">{title}</h3>
              <button onClick={onClose} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white" data-id="element-32">
                <X className="h-5 w-5" data-id="element-33" />
              </button>
            </div>
            <div className="whitespace-pre-line text-sm leading-relaxed text-gray-400" data-id="element-34">
              {content}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function ClosingSection() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <section className="border-t border-white/10 bg-[#0a0a0a] py-24 text-center" data-id="element-35">
        <div className="mx-auto max-w-2xl px-6" data-id="element-36">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-37">
            <h2 className="text-3xl font-bold text-white md:text-4xl" data-id="element-38">
              No necesitas que el algoritmo "te quiera".
            </h2>
            <p className="mt-6 text-2xl font-medium text-amber-500" data-id="element-39">
              Necesitas volver a controlarte tú.
            </p>

            <div className="mt-12" data-id="element-40">
              <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
              behavior: 'smooth'
            })} className="rounded bg-white px-8 py-4 text-lg font-bold text-black transition-transform hover:scale-105" data-id="element-41">
                Descargar Kit —{' '}
                <span className="line-through opacity-50" data-id="element-42">$19</span> $6.36{' '}
                <span className="text-green-600 text-sm font-bold" data-id="element-43">(-67%)</span>
              </button>
            </div>

            <p className="mt-12 text-sm text-gray-600" data-id="element-44">
              © 2026 Shadowban Survival Kit. All rights reserved.
            </p>

            <div className="mt-4 flex items-center justify-center gap-4 text-xs" data-id="element-45">
              <button onClick={() => setActiveModal('privacy')} className="text-gray-500 underline decoration-gray-700 underline-offset-2 transition-colors hover:text-gray-300" data-id="element-46">
                Política de Privacidad
              </button>
              <span className="text-gray-700" data-id="element-47">•</span>
              <button onClick={() => setActiveModal('terms')} className="text-gray-500 underline decoration-gray-700 underline-offset-2 transition-colors hover:text-gray-300" data-id="element-48">
                Términos y Condiciones
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" content={privacyContent} data-id="element-49" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={termsContent} data-id="element-50" />
    </>;
}